import { client, handleRequest, handleRequestBody } from "./client.ts";

export type InviteRole = "engineer" | "almoxarife" | "funcionario" | "cliente";

export type InviteStatus = "pending" | "accepted";

export interface Invite {
	id: string;
	account_id: string;
	invited_by_user_id?: string;
	email: string;
	role: InviteRole;
	worksite_id?: string;
	status: InviteStatus;
	expires_at: string;
	accepted_at?: string;
	created_at: string;
	updated_at: string;
}

export interface ListInvitesParams {
	status?: InviteStatus | "all";
	worksiteId?: string;
}

export interface ListInvitesResponse {
	count: number;
	items: Invite[];
}

export function listInvites(
	params: ListInvitesParams = {},
): Promise<ListInvitesResponse> {
	return handleRequestBody(() =>
		client.get<ListInvitesResponse>("/invites", {
			params: { status: params.status, worksite_id: params.worksiteId },
		}),
	);
}

export interface CreateInviteInput {
	email: string;
	role: InviteRole;
	worksiteId?: string;
}

interface InviteResponse {
	invite: Invite;
}

export async function createInvite(input: CreateInviteInput): Promise<Invite> {
	const { invite } = await handleRequestBody(() =>
		client.post<InviteResponse>("/invites", {
			email: input.email,
			role: input.role,
			worksite_id: input.worksiteId,
		}),
	);
	return invite;
}

export async function resendInvite(id: string): Promise<Invite> {
	const { invite } = await handleRequestBody(() =>
		client.post<InviteResponse>(`/invites/${id}/resend`, {}),
	);
	return invite;
}

export async function deleteInvite(id: string): Promise<void> {
	await handleRequest(() => client.delete(`/invites/${id}`));
}

export interface InvitePreview {
	email: string;
	role: InviteRole;
	role_label: string;
	account_name: string;
	worksite_name?: string;
	inviter_name?: string;
	expires_at: string;
	user_exists: boolean;
}

export function previewInvite(token: string): Promise<InvitePreview> {
	return handleRequestBody(() =>
		client.get<InvitePreview>(`/invites/${token}`),
	);
}

export interface AcceptInviteInput {
	name: string;
	password: string;
}

export interface AcceptInviteResponse {
	jwt: string;
	account_id: string;
	role: InviteRole;
}

export function acceptInvite(
	token: string,
	input: AcceptInviteInput,
): Promise<AcceptInviteResponse> {
	return handleRequestBody(() =>
		client.post<AcceptInviteResponse>(`/invites/${token}/accept`, input),
	);
}
